import { LanguagesEnum } from '@/api/enums/languages.enum';
import {
  EmailAttachment,
  InteractiveElementType,
  MediaSettings,
  Slide,
  SlideMedia,
  SlideTemplateOptions,
  SlideTemplateType,
  TextAlignment,
} from '@/client/courses/types/courses.type';
import {
  AiCustomizationPanel,
  ContentMediaUpload,
  EditorSidebar,
  EditorSidebarLabel,
  EditorSidebarPanel,
  LanguageSettingsPanel,
  MediaSettingsPanel,
} from '@/components/content-editor/sidebar';
import { AttachmentTypeMenu } from '@/components/slides/admin/AttachmentTypeMenu';
import { useAppSelector } from '@/hooks/store';
import { selectCurrentAccount } from '@/store/features/account';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import { isVideoFile } from '@/utils/helpers/content-media.helper';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { OverlayPanel } from 'primereact/overlaypanel';
import { TabMenu, TabMenuTabChangeEvent } from 'primereact/tabmenu';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const InfoBoxSidebarSection = styled.div`
  margin-bottom: 32px;
`;

const StyledPanel = styled(EditorSidebarPanel)`
  .border-checkbox {
    color: var(--black-main);
  }

  .slide-type-panel {
    .p-dropdown-items-wrapper {
      display: none !important;
    }
  }
`;

const StyledOverlayPanel = styled(OverlayPanel)`
  box-shadow: var(--box-shadow-black);
  .p-overlaypanel-content {
    padding: 0;
    border-radius: var(--small-border-radius);
    width: 400px;
    display: flex;
    flex-wrap: wrap;

    .template-option {
      cursor: pointer;
      flex: 1 1 40%;
      font-size: var(--xsmall-font-size);
      line-height: var(--xsmall-line-height);
      font-weight: 500;
      padding: var(--medium-padding);
      transition: all 0.2s ease;
      &:hover {
        background: var(--gray-pale);
      }
    }
  }
`;

const StyledAlignmentTabMenu = styled(TabMenu)`
  height: auto;
  padding: 0;

  .p-tabmenu-nav {
    gap: var(--xsmall-padding);
  }

  .p-tabmenuitem {
    background-color: var(--beige-main);
    padding: var(--xsmall-padding) 7px;
    border-radius: var(--xxxsmall-border-radius);
    transition: all 0.2s ease;

    &.p-highlight {
      .p-menuitem-link {
        color: var(--red-main) !important;

        .p-menuitem-icon {
          color: var(--red-main) !important;
        }
      }
    }

    &:not(.p-highlight):hover {
      background-color: var(--gray-pale);
    }
  }

  .p-menuitem-link {
    padding: 0 !important;
    border: none !important;
    font-weight: 500;
    color: var(--black-main);
    transition: color 0.2s ease;

    .p-menuitem-icon {
      margin: 0;
      border: none !important;
      font-weight: 500;
      color: var(--black-main);
      transition: color 0.2s ease;
    }
  }
`;

const StyledAddImageButton = styled(FlexContainer)`
  border: 1px solid var(--gray-moderate);
  border-radius: var(--xsmall-border-radius);
  padding: var(--small-padding);
  cursor: pointer;
  color: var(--black-main);
  font-weight: 500;
  font-size: var(--xsmall-font-size);
  line-height: var(--xsmall-line-height);
  transition: all 0.2s ease;

  &:hover {
    background: var(--beige-main);
  }
`;

const StyledOption = styled(FlexContainer)`
  color: var(--black-main);
  font-weight: 500;
  font-size: var(--xsmall-font-size);
  line-height: var(--xsmall-line-height);
`;

type SlideDeckSidebarProps = {
  selectedLanguage: LanguagesEnum;
  courseLanguages: LanguagesEnum[];
  currentSlideData: any;
  popUpSlides: Slide[];
  isEditingPopUpSlide: boolean;
  isCustomContent?: boolean;
  onLanguageChange: (language: LanguagesEnum) => void;
  onSlideUpdate: (slideId: string, updates: Partial<Slide>) => void;
  onCopyToLanguage: (targetLanguages: LanguagesEnum[]) => void;
  onSave: () => void;
  isSaveDisabled?: boolean;
};

export const SlideDeckSidebar: React.FC<SlideDeckSidebarProps> = ({
  selectedLanguage,
  courseLanguages,
  currentSlideData,
  popUpSlides,
  isEditingPopUpSlide,
  isCustomContent,
  onLanguageChange,
  onSlideUpdate,
  onCopyToLanguage,
  onSave,
  isSaveDisabled,
}) => {
  const { t } = useTranslation();
  const account = useAppSelector(selectCurrentAccount);
  const op = useRef<OverlayPanel>(null);

  const [isUploading, setIsUploading] = useState(false);
  const [openImageSettings, setOpenImageSettings] = useState<number | null>(
    null,
  );
  const getPopupSlideId = (slide: Slide): string | undefined => {
    if (slide.popupSlide) {
      return typeof slide.popupSlide === 'string'
        ? slide.popupSlide
        : slide.popupSlide.id;
    }
    return slide.tempPopupSlideId;
  };

  const handlePopupSlideChange = (selectedValue: string | null | undefined) => {
    if (!currentSlideData.id) return;

    if (!selectedValue) {
      onSlideUpdate(currentSlideData.id, {
        popupSlide: undefined,
        tempPopupSlideId: undefined,
      });
      return;
    }

    const selectedPopupSlide = popUpSlides.find(
      ({ id, tempId }) =>
        (id?.includes('temp-') ? tempId : id) === selectedValue,
    );

    if (selectedPopupSlide) {
      const isNewSlide = selectedPopupSlide.id?.includes('temp-');

      onSlideUpdate(currentSlideData.id, {
        tempPopupSlideId: isNewSlide ? selectedPopupSlide.tempId : undefined,
        popupSlide: isNewSlide ? undefined : selectedPopupSlide.id,
      });
    }
  };

  const optionTemplate = (item: {
    value: SlideTemplateType;
    label: string;
    image: string;
  }) => {
    return (
      <StyledOption
        direction="column"
        align="center"
        gap={8}
        onClick={() => {
          const updates: Partial<Slide> = {
            templateType: item.value,
            textAlignment: [
              SlideTemplateType.TEXT_IMAGE,
              SlideTemplateType.SUMMARY,
            ].includes(item.value)
              ? TextAlignment.LEFT
              : currentSlideData.textAlignment || TextAlignment.LEFT,
          };

          if (item.value === SlideTemplateType.GALLERY) {
            updates.interactiveElement = null;
          }

          onSlideUpdate(currentSlideData.id, updates);
          op.current?.hide();
        }}
        className="template-option"
      >
        <img src={item.image} alt={item.label} />
        <div>{item.label}</div>
      </StyledOption>
    );
  };
  const mediaSettingsTemplate = (image: SlideMedia) => {
    return (
      <MediaSettingsPanel
        media={image}
        isOpen={openImageSettings === image.position}
        onOpen={() => setOpenImageSettings(image.position)}
        onClose={() => setOpenImageSettings(null)}
        onChange={(settings: MediaSettings) => {
          if (!currentSlideData.id) return;

          onSlideUpdate(currentSlideData.id, {
            images: currentSlideData.images?.map((img: SlideMedia) =>
              img.position === image.position ? { ...img, ...settings } : img,
            ),
          });
        }}
      />
    );
  };

  const slideAttachment: EmailAttachment | null =
    currentSlideData.interactiveElement?.data?.attachment || null;

  const setSlideAttachment = (attachment: EmailAttachment | null) => {
    if (!currentSlideData.id) return;

    const data = currentSlideData.interactiveElement?.data || {};

    onSlideUpdate(currentSlideData.id, {
      interactiveElement: {
        ...currentSlideData.interactiveElement,
        data: {
          ...data,
          attachment: attachment || undefined,
        },
      },
    });
  };

  return (
    <EditorSidebar>
      <h2>{t('content.presentation.editor')}</h2>

      <LanguageSettingsPanel
        selectedLanguage={selectedLanguage}
        languages={courseLanguages}
        onLanguageChange={onLanguageChange}
        onCopyToLanguage={onCopyToLanguage}
      />

      {/* Template Settings */}
      <StyledPanel
        header={t('content.template.settings')}
        toggleable
        collapsed={false}
        className="w-full"
        collapseIcon="pi pi-chevron-up"
        expandIcon="pi pi-chevron-down"
      >
        <AppButton
          className="w-full mb-3"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
            op.current?.toggle(e)
          }
          label={
            currentSlideData.templateType
              ? t(`content.slide.template.${currentSlideData.templateType}`)
              : t('content.slide.template.text-image')
          }
          icon="pi pi-chevron-down"
          iconPos="right"
          severity="dropdown"
        />

        {/* Template Type Selection */}
        <StyledOverlayPanel ref={op} showCloseIcon={false}>
          {SlideTemplateOptions(t).map((option) => optionTemplate(option))}
        </StyledOverlayPanel>

        <FlexContainer direction="column" gap={18}>
          {currentSlideData.images && currentSlideData.images.length > 0 ? (
            currentSlideData.images
              ?.filter((image: SlideMedia) =>
                currentSlideData.templateType !== SlideTemplateType.GALLERY
                  ? image.position === 0
                  : true,
              )
              .map((image: SlideMedia) => (
                <React.Fragment
                  key={`${selectedLanguage}-${
                    currentSlideData.id || 'current'
                  }-image-${image.position}`}
                >
                  <ContentMediaUpload
                    preselectedFile={image.file}
                    preselectedInteractiveElement={
                      currentSlideData.interactiveElement || null
                    }
                    setIsSubmitDisabled={setIsUploading}
                    additionalImgOptions={
                      <>
                        {mediaSettingsTemplate(image)}
                        {currentSlideData.interactiveElement?.type ===
                          InteractiveElementType.EMAIL && (
                          <AttachmentTypeMenu
                            attachment={slideAttachment}
                            onChange={setSlideAttachment}
                            menuId={`slide-attachment-type-menu-${
                              currentSlideData.id || 'new'
                            }-${image.position}`}
                            triggerClassName="m-auto"
                          />
                        )}
                      </>
                    }
                    itemId={currentSlideData.id}
                    templateType={currentSlideData.templateType}
                    onInteractiveElementAdd={(slideId, interactiveElement) => {
                      onSlideUpdate(slideId, {
                        interactiveElement,
                        // Clear images when adding interactive element
                        images: undefined,
                      });
                    }}
                    onFileUpload={(file) => {
                      if (!currentSlideData.id) return;

                      const existingImages = [
                        ...(currentSlideData.images || []),
                      ];

                      if (file) {
                        // Update existing image
                        const imageIndex = existingImages.findIndex(
                          (img) => img.position === image.position,
                        );
                        if (imageIndex !== -1) {
                          existingImages[imageIndex] = {
                            ...existingImages[imageIndex],
                            file,
                          };
                          onSlideUpdate(currentSlideData.id, {
                            images: existingImages,
                            // Clear interactive element when uploading image
                            interactiveElement: undefined,
                          });
                          if (isVideoFile(file)) {
                            setOpenImageSettings(image.position);
                          }
                        }
                      } else {
                        // Remove image and reindex positions
                        const filteredImages = existingImages
                          .filter((img) => img.position !== image.position)
                          .map((img, index) => ({ ...img, position: index }));

                        onSlideUpdate(currentSlideData.id, {
                          images:
                            filteredImages.length > 0
                              ? filteredImages
                              : undefined,
                          // Clear interactive element when clearing image
                          interactiveElement: undefined,
                        });
                      }
                    }}
                  />
                </React.Fragment>
              ))
          ) : (
            <>
              <ContentMediaUpload
                key={`${selectedLanguage}-${
                  currentSlideData.id || 'current'
                }-image`}
                preselectedFile={currentSlideData.image}
                preselectedInteractiveElement={
                  currentSlideData.interactiveElement || null
                }
                setIsSubmitDisabled={setIsUploading}
                additionalImgOptions={
                  currentSlideData.interactiveElement?.type ===
                  InteractiveElementType.EMAIL ? (
                    <AttachmentTypeMenu
                      attachment={slideAttachment}
                      onChange={setSlideAttachment}
                      menuId={`slide-attachment-type-menu-${
                        currentSlideData.id || 'new'
                      }-0`}
                      triggerClassName="m-auto"
                    />
                  ) : null
                }
                itemId={currentSlideData.id}
                templateType={currentSlideData.templateType}
                onInteractiveElementAdd={(slideId, interactiveElement) => {
                  onSlideUpdate(slideId, {
                    interactiveElement,
                    // Clear images when adding interactive element
                    images: undefined,
                  });
                }}
                onFileUpload={(file) => {
                  if (file && currentSlideData.id) {
                    onSlideUpdate(currentSlideData.id, {
                      images: [{ file, position: 0, withBorder: false }],
                      // Clear interactive element when uploading image
                      interactiveElement: undefined,
                    });
                    if (isVideoFile(file)) {
                      setOpenImageSettings(0);
                    }
                  } else if (currentSlideData.id) {
                    onSlideUpdate(currentSlideData.id, {
                      images: [{ file: null, position: 0, withBorder: false }],
                      interactiveElement: undefined,
                    });
                  }
                }}
              />
            </>
          )}
          {SlideTemplateType.GALLERY === currentSlideData.templateType && (
            <StyledAddImageButton
              direction="column"
              gap={8}
              onClick={() => {
                const existingImages = currentSlideData.images || [];
                const nextPosition =
                  existingImages.length > 0
                    ? Math.max(
                        ...existingImages.map(
                          (img: SlideMedia) => img.position,
                        ),
                      ) + 1
                    : 0;
                onSlideUpdate(currentSlideData.id, {
                  images: [
                    ...existingImages,
                    {
                      file: null,
                      position: nextPosition,
                      withBorder: true,
                      withZoom: false,
                    },
                  ],
                });
              }}
            >
              <i className="pi pi-plus" />
              <div>{t('content.image.upload.add.image')}</div>
            </StyledAddImageButton>
          )}
        </FlexContainer>

        {/* Text Alignment Settings */}
        {[SlideTemplateType.CHAPTER, SlideTemplateType.GALLERY].includes(
          currentSlideData.templateType,
        ) && (
          <FlexContainer direction="column" align="flex-start" className="mt-3">
            <EditorSidebarLabel>
              {t('content.text.alignment')}
            </EditorSidebarLabel>
            <StyledAlignmentTabMenu
              key={`alignment-${currentSlideData.id}-${
                currentSlideData.textAlignment || 'left'
              }`}
              activeIndex={
                currentSlideData.textAlignment === TextAlignment.CENTER ? 1 : 0
              }
              onTabChange={(e: TabMenuTabChangeEvent) => {
                const newAlignment =
                  e.index === 1 ? TextAlignment.CENTER : TextAlignment.LEFT;
                onSlideUpdate(currentSlideData.id, {
                  textAlignment: newAlignment,
                });
              }}
              model={[
                {
                  icon: 'pi pi-align-left',
                },
                {
                  icon: 'pi pi-align-center',
                },
              ]}
            />
          </FlexContainer>
        )}
      </StyledPanel>

      {/* Info Box Settings */}
      {!isEditingPopUpSlide &&
        [SlideTemplateType.TEXT_IMAGE, SlideTemplateType.SUMMARY].includes(
          currentSlideData.templateType,
        ) && (
          <StyledPanel
            header={t('content.info.box')}
            toggleable
            collapsed={false}
            className="w-full"
            collapseIcon="pi pi-chevron-up"
            expandIcon="pi pi-chevron-down"
          >
            <InfoBoxSidebarSection>
              <EditorSidebarLabel>
                {t('content.info.box.text')}
              </EditorSidebarLabel>
              <InputText
                className="w-full"
                placeholder={t('content.enter.info.box.text')}
                style={{ marginBottom: 20 }}
                value={currentSlideData.infobox || ''}
                onChange={(e) => {
                  if (currentSlideData.id) {
                    onSlideUpdate(currentSlideData.id, {
                      infobox: e.target.value,
                    });
                  }
                }}
              />
              <EditorSidebarLabel>
                {t('content.link.to.slide')}
              </EditorSidebarLabel>
              <Dropdown
                value={getPopupSlideId(currentSlideData)}
                optionValue="value"
                options={popUpSlides.map((s) => ({
                  label: s.headline?.replace(/<[^>]*>?/g, ''),
                  value: s?.id?.includes('temp-') ? s.tempId : s.id,
                }))}
                placeholder={t('content.select.info.box.slide')}
                onChange={(e) => handlePopupSlideChange(e.value)}
                showClear
                appendTo={document.body}
                className="w-full"
              />
            </InfoBoxSidebarSection>
          </StyledPanel>
        )}

      {account?.isSystem && !isCustomContent && (
        <AiCustomizationPanel
          enabled={currentSlideData.isAiCustomizationEnabled !== false}
          instructions={currentSlideData.aiCustomizationInstructions || ''}
          onToggle={(enabled) => {
            if (currentSlideData.id) {
              onSlideUpdate(currentSlideData.id, {
                isAiCustomizationEnabled: enabled,
              });
            }
          }}
          onInstructionsChange={(instructions) => {
            if (currentSlideData.id) {
              onSlideUpdate(currentSlideData.id, {
                aiCustomizationInstructions: instructions,
              });
            }
          }}
          tooltipTarget="ai-instructions-tooltip"
          tooltipKey="content.slide.aiCustomizationInstructions.tooltip"
          disabledKey="content.slide.aiCustomizationInstructions.disabled"
        />
      )}

      {/* Actions */}
      <AppButton
        label={t('content.save.presentation')}
        style={{ marginTop: 'auto' }}
        isDisabled={isUploading || isSaveDisabled}
        onClick={onSave}
      />
    </EditorSidebar>
  );
};
